import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';

import { openFeedbackDialog, openPreconditionsDialog } from '@/features/app/MessageHandler';
import { ENGINE_OIL_STATE, SOURCE_DEVICE, STATUS } from '@/features/engineoil/utils/constants';
import { getIconColor, getIconName, getThresholdClass, isEngineOilOutdated } from '@/features/engineoil/utils/utils';
import type { EngineOil } from '@/generated/GeneratedApiTypes';
import AnalyticsEvents from '@/utils/analytics/AnalyticsEvents';
import ActiveDtcTag from './ActiveDtcTag';
import BasePanel from './BasePanel';
import ReadingNotPossible from './ReadingNotPossible';

const Tbm3DidSCSCard = ({ engineOil }: { engineOil: EngineOil }) => {
    const intl = useIntl();
    const { state, status, measured_at, active_dtc } = engineOil;

    const isReadingNotPossible = isEngineOilOutdated(measured_at.date);
    const hasActiveDtc = active_dtc != null;
    const iconName = getIconName(status);
    const calloutVariant =
        state !== ENGINE_OIL_STATE.OK ? getThresholdClass(status, isReadingNotPossible, hasActiveDtc) : '';

    return (
        <BasePanel
            timestamp={measured_at.date}
            mileage={measured_at.mileage_km}
            isReadingNotPossible={isReadingNotPossible}
            testId="card-customer-tbm3-did-service_care_s"
        >
            <div className="space-y-10">
                {hasActiveDtc && <ActiveDtcTag dtcType={active_dtc!.type} />}

                {isReadingNotPossible && (
                    <ReadingNotPossible
                        onHoverIntent={() => AnalyticsEvents.hoverDidSidebarOlder24h(SOURCE_DEVICE.TBM3)}
                    />
                )}
            </div>

            <div
                className={`display-flex align-items-center ${
                    calloutVariant ? `callout callout-${calloutVariant}` : ''
                } padding-10 margin-top-10 margin-bottom-10`}
            >
                <div className={`display-flex align-items-center ${status === STATUS.NORMAL ? 'padding-left-10' : ''}`}>
                    {iconName && (
                        <span
                            className={`uikit-icon ${iconName} ${getIconColor(status)} text-size-14 margin-right-5`}
                        />
                    )}
                    <span className="text-size-14 text-color-darkest">
                        <FormattedMessage id={`intl-msg:engineOil.state.${state}`} />
                    </span>
                </div>

                <div className="display-flex align-items-center margin-left-auto padding-left-10">
                    <a
                        href="#"
                        className="text-color-primary text-right"
                        onClick={(e) => {
                            e.preventDefault();
                            AnalyticsEvents.openDidSPreconditionsDialog(SOURCE_DEVICE.TBM3);
                            openPreconditionsDialog(
                                engineOil.asset_id,
                                intl.formatMessage({ id: 'intl-msg:engineOil.preconditions.dialog.title' }),
                            );
                        }}
                    >
                        <FormattedMessage id="intl-msg:engineOil.seeReadingPreconditions" />
                    </a>
                    <span className="text-color-primary" style={{ padding: '1px 8px', flexShrink: 0 }}>
                        |
                    </span>
                    <Button
                        className="white-space-nowrap"
                        style={{ flexShrink: 0 }}
                        onClick={() => {
                            AnalyticsEvents.openDidFeedbackEngineOil(SOURCE_DEVICE.TBM3);
                            openFeedbackDialog();
                        }}
                    >
                        <span className="uikit-icon icon-thumbs-up" />
                        <FormattedMessage id="intl-msg:engineOil.tooltip.rateYourExperience" />
                    </Button>
                </div>
            </div>
        </BasePanel>
    );
};

export default Tbm3DidSCSCard;
